import { useState } from 'react';
import useReveal from '../hooks/useReveal';
import './Blog.css';

const POSTS = [
  {
    slug:'web-design-trends-2025',
    cat:'Design',
    title:'10 Web Design Trends Dominating 2025',
    excerpt:'From bento grids to glassmorphism 2.0 - discover the design trends reshaping the web this year and how to use them effectively.',
    author:'Priya Patel',
    init:'P',
    read:'5 min read',
    date:'Jan 15, 2025',
    featured: true,
  },
  {
    slug:'seo-strategy-2025',
    cat:'Marketing',
    title:'The Ultimate SEO Strategy Guide for 2025',
    excerpt:'Search algorithms have evolved. Here is how to rank higher with AI-first content and technical SEO mastery.',
    author:'Neha Gupta',
    init:'N',
    read:'8 min read',
    date:'Jan 08, 2025',
    featured: false,
  },
  {
    slug:'react-performance',
    cat:'Development',
    title:'React Performance Optimization: 15 Pro Tips',
    excerpt:'Lazy loading, memoization, virtualization - master these techniques for blazing-fast React applications.',
    author:'Rohit Shah',
    init:'R',
    read:'10 min read',
    date:'Dec 28, 2024',
    featured: false,
  },
  {
    slug:'brand-identity-guide',
    cat:'Design',
    title:'Building a Brand Identity That Lasts',
    excerpt:'A strong brand is more than a logo. Learn how to build a cohesive visual identity system from the ground up.',
    author:'Priya Patel',
    init:'P',
    read:'6 min read',
    date:'Dec 20, 2024',
    featured: false,
  },
  {
    slug:'nextjs-vs-gatsby',
    cat:'Development',
    title:'Next.js vs Gatsby: Which Should You Choose in 2025?',
    excerpt:'A deep dive into the pros and cons of the two most popular React-based frameworks - with real benchmark data.',
    author:'Rohit Shah',
    init:'R',
    read:'7 min read',
    date:'Dec 12, 2024',
    featured: false,
  },
  {
    slug:'social-media-roi',
    cat:'Marketing',
    title:'Measuring Social Media ROI: The Complete Guide',
    excerpt:'Stop guessing. Here is exactly how to measure, report, and improve the return on your social media investment.',
    author:'Neha Gupta',
    init:'N',
    read:'5 min read',
    date:'Dec 05, 2024',
    featured: false,
  },
  {
    slug:'figma-advanced-tips',
    cat:'Design',
    title:'Advanced Figma Techniques Every Designer Should Know',
    excerpt:'Variables, component properties, and auto-layout mastery - level up your Figma workflow with these pro techniques.',
    author:'Priya Patel',
    init:'P',
    read:'9 min read',
    date:'Nov 28, 2024',
    featured: false,
  },
  {
    slug:'ecommerce-conversion',
    cat:'Marketing',
    title:'7 E-Commerce Conversion Killers (and How to Fix Them)',
    excerpt:'Your traffic is fine. Your product is great. So why are people not buying? These 7 hidden issues might be the reason.',
    author:'Neha Gupta',
    init:'N',
    read:'6 min read',
    date:'Nov 20, 2024',
    featured: false,
  },
];

const CATS = ['All','Design','Development','Marketing'];

const CAT_COLORS = {
  Design: 'var(--gold)',
  Development: 'var(--accent2)',
  Marketing: 'var(--accent)',
};

export default function Blog(){
  useReveal();
  const [active, setActive] = useState('All');

  const filtered = active === 'All' ? POSTS : POSTS.filter(p => p.cat === active);
  const featured = filtered.find(p => p.featured) || filtered[0];
  const rest     = filtered.filter(p => p !== featured);

  return(
    <div className="blog-page">

      {/* PAGE HERO */}
      <div className="page-hero">
        <span className="ph-eye">Knowledge Hub</span>
        <h1 className="ph-title">Insights from the <span className="g">CubeX Team</span></h1>
        <p className="ph-sub">Design, development, and digital marketing insights to help your business grow smarter and faster.</p>
      </div>

      {/* CATEGORY FILTERS */}
      <section className="blog-sect">
        <div className="container">
          <div className="blog-filters reveal">
            {CATS.map(c=>(
              <button
                key={c}
                className={`filter-btn${active===c?' filter-btn--active':''}`}
                onClick={()=>setActive(c)}
              >
                {c}
              </button>
            ))}
          </div>

          {/* FEATURED + GRID */}
          <div className="blog-layout">

            {/* Featured post */}
            {featured && (
              <div className="blog-featured reveal">
                <div className="post-cat" style={{color: CAT_COLORS[featured.cat] || 'var(--gold)'}}>
                  <span className="dot" style={{background: CAT_COLORS[featured.cat] || 'var(--gold)'}}/>
                  {featured.cat}
                </div>
                <h2 className="featured-title">{featured.title}</h2>
                <p className="featured-excerpt">{featured.excerpt}</p>
                <div className="post-meta">
                  <div className="post-author">
                    <div className="post-avatar">{featured.init}</div>
                    <span>{featured.author}</span>
                  </div>
                  <div className="post-right">
                    <span>🕐 {featured.read}</span>
                    <span className="meta-sep">·</span>
                    <span>{featured.date}</span>
                  </div>
                </div>
                <a href={`/blog/${featured.slug}`} className="blog-read-btn">
                  Read Article <span className="arrow">→</span>
                </a>
              </div>
            )}

            {/* Post grid */}
            <div className="blog-grid">
              {rest.map((post, i) => (
                <a
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                  className={`post-card reveal d${(i % 4) + 1}`}
                >
                  <div className="post-card__top">
                    <div className="post-cat post-cat--sm" style={{color: CAT_COLORS[post.cat] || 'var(--gold)'}}>
                      <span className="dot" style={{background: CAT_COLORS[post.cat] || 'var(--gold)'}}/>
                      {post.cat}
                    </div>
                    <span className="post-date">{post.date}</span>
                  </div>
                  <h3 className="post-card__title">{post.title}</h3>
                  <p className="post-card__excerpt">{post.excerpt}</p>
                  <div className="post-meta post-meta--sm">
                    <div className="post-author">
                      <div className="post-avatar post-avatar--sm">{post.init}</div>
                      <span>{post.author}</span>
                    </div>
                    <span className="post-read">🕐 {post.read}</span>
                  </div>
                </a>
              ))}
            </div>

          </div>

          {/* Load more placeholder */}
          <div className="blog-more reveal">
            <button className="btn-ghost" style={{padding:'13px 40px'}}>Load More Articles</button>
          </div>

        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <div className="newsletter-band">
        <div className="newsletter-glow"/>
        <div className="container">
          <div className="newsletter-inner reveal">
            <div className="newsletter-content">
              <span className="s-eye">Stay Updated</span>
              <h3 className="newsletter-title">Get Weekly Insights <span className="g">Delivered</span></h3>
              <p className="newsletter-sub">Join 2,000+ professionals who get our best digital strategy insights every week.</p>
            </div>
            <form className="newsletter-form" onSubmit={e=>e.preventDefault()}>
              <input
                className="newsletter-input"
                type="email"
                placeholder="Enter your email address…"
              />
              <button type="submit" className="btn-primary" style={{flexShrink:0}}>
                Subscribe ✦
              </button>
            </form>
          </div>
        </div>
      </div>

    </div>
  );
}